import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Loader } from '../components/common';
import { TaskCard } from '../components/tasks/TaskCard';
import { useTaskStore } from '../stores/taskStore';
import { useUIStore } from '../stores/uiStore';
import type { Task } from '../types';

export const TodayPage: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  // Use stable selectors to prevent re-renders from unstable references
  const tasks = useTaskStore((state) => state.tasks);
  const fetchTasks = useTaskStore((state) => state.fetchTasks);
  const setFilters = useTaskStore((state) => state.setFilters);
  const isLoading = useTaskStore((state) => state.isLoading);
  
  // Track if we've done the initial fetch for this route to prevent duplicates
  const initialFetchDone = useRef(false);
  const lastPath = useRef(location.pathname);

  // Set today's date filter and fetch tasks - runs on mount and route change
  // Backend handles all filtering: dueDate=today + overdue tasks
  // Frontend just passes view='today' and sorts overdue tasks at top
  useEffect(() => {
    // Only fetch if path changed (route navigation) or this is the first mount
    if (lastPath.current === location.pathname && initialFetchDone.current) {
      return;
    }
    
    lastPath.current = location.pathname;
    initialFetchDone.current = true;
    
    // Backend handles all filtering for today view
    const pageFilters = { view: 'today' };
    
    // Store filters in state so Header search can preserve them
    setFilters(pageFilters);
    
    // Fetch with the filters directly (don't rely on state which isn't updated yet)
    fetchTasks(pageFilters);
  }, [location.pathname]); // Minimal dependencies - only path change triggers fetch

  // Separate tasks into overdue, today's pending, and completed within 24h
  // Sort: overdue at top, then pending, then completed
  const { overdueTasks, pendingTasks, completedTasks } = useMemo(() => {
    const overdue: Task[] = []; // Overdue tasks (dueDate < today, not completed)
    const pending: Task[] = []; // Tasks due today, not completed
    const completed: Task[] = []; // Completed within 24 hours
    
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000 - 1);

    tasks.forEach(task => {
      if (task.completed) {
        // Check if completed within last 24 hours
        if (task.completedAt && new Date(task.completedAt) >= twentyFourHoursAgo) {
          completed.push(task);
        }
      } else if (task.dueDate) {
        const taskDueDate = new Date(task.dueDate);
        if (taskDueDate < todayStart) {
          // Overdue: due date is before today
          overdue.push(task);
        } else if (taskDueDate >= todayStart && taskDueDate <= todayEnd) {
          // Due today
          pending.push(task);
        }
      }
    });

    // Sort overdue tasks by due date (most overdue first = oldest due date)
    overdue.sort((a, b) => {
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
      return dateA - dateB; // Oldest first (most overdue)
    });

    return { overdueTasks: overdue, pendingTasks: pending, completedTasks: completed };
  }, [tasks]);

  const totalTasks = overdueTasks.length + pendingTasks.length + completedTasks.length;
  const hasNoTasks = totalTasks === 0;

  return (
    <div className="flex flex-col h-full">
      <Header title={t('today.title')} showSearch />
      
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader size="md" />
          </div>
        ) : hasNoTasks ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="text-4xl mb-4">🎉</div>
            <div className="text-gray-900 dark:text-gray-100 font-medium mb-2">
              {t('today.emptyTitle')}
            </div>
            <p className="text-sm text-gray-500">
              {t('today.emptySubtitle')}
            </p>
            <button
              onClick={() => useUIStore.getState().openTaskModal()}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('today.createTask')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overdue Tasks Section - shown at top */}
            {overdueTasks.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-red-600 dark:text-red-400 uppercase tracking-wider">
                  {t('today.overdue')} ({overdueTasks.length})
                </h3>
                {overdueTasks.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            )}

            {/* Today's Pending Tasks Section */}
            {pendingTasks.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('today.todaySection')} ({pendingTasks.length})
                </h3>
                {pendingTasks.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            )}

            {/* Completed Tasks Section (within 24h) */}
            {completedTasks.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">
                  {t('today.completedSection')} ({completedTasks.length})
                </h3>
                {completedTasks.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default TodayPage;